import { Button, Input, ModalV2, Select, TextTooltip } from '@components'
import { Switch } from '@components/ui/Primitives'
import { VAT_PERIOD_OPTIONS } from '@constants'
import { FeatureWrapper, useBusinessContext } from '@context'
import { fetchBusiness, IBusiness, updateBusiness } from '@query'
import { APIError, getApiFieldErrors, getOptionLabelByValue, setAPIErrors } from '@utils'
import { compactIBAN, isGermanIBAN, tryFormatIBAN } from '@utils/iban'
import { Controller, get, useForm } from 'react-hook-form'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import {
  FaCalendarAlt,
  FaClock,
  FaLandmark,
  FaPercentage,
  FaQuestionCircle,
  FaSave,
  FaUniversity
} from 'react-icons/fa'
import { isGermanBusinessCountry } from '@pages/InvoicingPage/xrechnungDelivery'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { useVATPostingMethods } from '@root/query/hooks/useVATPostingMethods'
import { useVATReportingMethods } from '@root/query/hooks/useVATReportingMethods'
import { SettingsDataCard } from '@pages/SettingsPage/components/SettingsDataCard'
import { SettingsReadOnlyField } from '@pages/SettingsPage/components/SettingsReadOnlyField'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'

export const VatSettings: React.FC = () => {
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const [isVatPeriodModalVisible, setIsVatPeriodModalVisible] = useState(false)
  const [isVatMethodModalVisible, setIsVatMethodModalVisible] = useState(false)
  const [isFinanzamtModalVisible, setIsFinanzamtModalVisible] = useState(false)
  const [isIbanModalVisible, setIsIbanModalVisible] = useState(false)
  const { data: vatPostingMethods, isLoading: isLoadingVatPostingMethods } = useVATPostingMethods()
  const { data: vatReportingMethods, isLoading: isLoadingVatReportingMethods } =
    useVATReportingMethods()

  const getDefaultValues = useCallback(
    (data?: Partial<IBusiness>): Partial<IBusiness> => ({
      default_vat_period: data?.default_vat_period,
      vat_posting_method: data?.vat_posting_method,
      vat_reporting_method: data?.vat_reporting_method
    }),
    []
  )

  const { data, refetch } = useQuery({
    queryKey: [slug],
    queryFn: () => fetchBusiness({ slug }),
    refetchOnMount: false
  })
  const isGermanBusiness = isGermanBusinessCountry(data?.country)

  const form = useForm<Partial<IBusiness>>({
    defaultValues: async () => {
      const { data } = await refetch()
      return getDefaultValues(data)
    }
  })

  const {
    handleSubmit,
    setError,
    reset,
    control,
    formState: { isDirty, isSubmitting }
  } = form

  const selectedVatPeriodLabel = useMemo(() => {
    return getOptionLabelByValue(VAT_PERIOD_OPTIONS, data?.default_vat_period, {
      t,
      fallbackToValue: false
    })
  }, [data?.default_vat_period, t])

  const selectedVatPostingMethodLabel = useMemo(
    () => getOptionLabelByValue(vatPostingMethods, data?.vat_posting_method),
    [data?.vat_posting_method, vatPostingMethods]
  )

  const selectedVatReportingMethodLabel = useMemo(
    () => getOptionLabelByValue(vatReportingMethods, data?.vat_reporting_method),
    [data?.vat_reporting_method, vatReportingMethods]
  )

  const updateMutation = useMutation<IBusiness, unknown, Partial<IBusiness>>({
    mutationFn: data => updateBusiness({ slug }, data),
    ...{
      onSuccess: async data => {
        reset(getDefaultValues(data))
        await queryClient.invalidateQueries({ queryKey: [slug] })
        await queryClient.invalidateQueries({ queryKey: ['businesses'] })
        await queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods'] })
        toast.success(t('general.changesSaved'))
        setIsVatPeriodModalVisible(false)
        setIsVatMethodModalVisible(false)
      },
      onError: error => {
        const { status, data } = (error as any) ?? {}
        status === 400 && setAPIErrors(data, setError)
      }
    }
  })

  const dauerfristMutation = useMutation<IBusiness, unknown, boolean>({
    mutationFn: has_dauerfristverlaengerung =>
      updateBusiness({ slug }, { has_dauerfristverlaengerung }),
    ...{
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: [slug] })
        await queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods'] })
        toast.success(t('general.changesSaved'))
      },
      onError: () => {
        toast.error(t('error.oho'))
      }
    }
  })

  const {
    register: registerFinanzamt,
    handleSubmit: handleFinanzamtSubmit,
    reset: resetFinanzamt,
    setError: setFinanzamtError,
    formState: {
      errors: finanzamtErrors,
      isDirty: isFinanzamtDirty,
      isSubmitting: isFinanzamtSubmitting
    }
  } = useForm<{ finanzamtsnummer: string }>({
    defaultValues: { finanzamtsnummer: data?.finanzamtsnummer ?? '' }
  })

  const {
    register: registerIban,
    handleSubmit: handleIbanSubmit,
    reset: resetIban,
    setError: setIbanError,
    formState: { errors: ibanErrors, isDirty: isIbanDirty, isSubmitting: isIbanSubmitting }
  } = useForm<{ finanzkasse_iban: string; finanzkasse_recipient_name: string }>({
    defaultValues: {
      finanzkasse_iban: data?.finanzkasse_iban ?? '',
      finanzkasse_recipient_name: data?.finanzkasse_recipient_name ?? ''
    }
  })

  useEffect(() => {
    resetFinanzamt({ finanzamtsnummer: data?.finanzamtsnummer ?? '' })
  }, [data?.finanzamtsnummer, resetFinanzamt])

  useEffect(() => {
    resetIban({
      finanzkasse_iban: data?.finanzkasse_iban ?? '',
      finanzkasse_recipient_name: data?.finanzkasse_recipient_name ?? ''
    })
  }, [data?.finanzkasse_iban, data?.finanzkasse_recipient_name, resetIban])

  const finanzamtMutation = useMutation<IBusiness, unknown, { finanzamtsnummer: string }>({
    mutationFn: payload =>
      updateBusiness(
        { slug },
        { finanzamtsnummer: payload.finanzamtsnummer.replace(/\D/g, '') || null }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [slug] })
      await queryClient.invalidateQueries({ queryKey: [slug, 'vat_periods'] })
      toast.success(t('general.changesSaved'))
      setIsFinanzamtModalVisible(false)
    },
    onError: error => {
      const { status, data: errorData } =
        (error as { status?: number; data?: APIError } | undefined) ?? {}
      if (status === 400 && errorData) {
        const fieldErrors = getApiFieldErrors(errorData)
        if (fieldErrors) {
          setAPIErrors(fieldErrors, setFinanzamtError)
          return
        }
      }
      toast.error(t('error.oho'))
    }
  })

  const ibanMutation = useMutation<
    IBusiness,
    unknown,
    { finanzkasse_iban: string; finanzkasse_recipient_name: string }
  >({
    mutationFn: payload =>
      updateBusiness(
        { slug },
        {
          finanzkasse_iban: compactIBAN(payload.finanzkasse_iban),
          finanzkasse_recipient_name: payload.finanzkasse_recipient_name.trim()
        }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [slug] })
      toast.success(t('general.changesSaved'))
      setIsIbanModalVisible(false)
    },
    onError: error => {
      const { status, data: errorData } =
        (error as { status?: number; data?: APIError } | undefined) ?? {}
      if (status === 400 && errorData) {
        const fieldErrors = getApiFieldErrors(errorData)
        if (fieldErrors) {
          setAPIErrors(fieldErrors, setIbanError)
          return
        }
      }
      toast.error(t('error.oho'))
    }
  })

  const onSubmit = handleSubmit(data => updateMutation.mutateAsync(data))
  const onFinanzamtSubmit = handleFinanzamtSubmit(payload => finanzamtMutation.mutateAsync(payload))
  const onIbanSubmit = handleIbanSubmit(payload => ibanMutation.mutateAsync(payload))

  const validateIban = (value: string) => {
    const compact = compactIBAN(value || '')
    if (!compact) {
      return t('validation.required')
    }
    if (!compact.startsWith('DE')) {
      return t('settings.finanzkasseIban.mustBeGerman')
    }
    if (!isGermanIBAN(compact)) {
      return t('settings.finanzkasseIban.invalid')
    }
    const invoicing = compactIBAN(data?.invoicing_iban || '')
    if (invoicing && invoicing === compact) {
      return t('settings.finanzkasseIban.sameAsInvoicing')
    }
    return true
  }

  return (
    <SettingsSubPageLayout title={t('settings.sections.vatSettings')}>
      <CardsWrapper>
        <SettingsDataCard
          icon={<FaCalendarAlt />}
          title={t('settings.vatPeriod')}
          action={
            <Button
              isSecondary
              size="sm"
              onClick={() => {
                reset(getDefaultValues(data))
                setIsVatPeriodModalVisible(true)
              }}
            >
              {t('general.edit')}
            </Button>
          }
        >
          <SettingsReadOnlyField label={t('settings.vatPeriod')} value={selectedVatPeriodLabel} />
        </SettingsDataCard>

        {isGermanBusiness && (
          <>
            <SettingsDataCard
              icon={<FaLandmark />}
              title={t('settings.finanzamtsnummer.title')}
              action={
                <Button
                  isSecondary
                  size="sm"
                  onClick={() => {
                    resetFinanzamt({ finanzamtsnummer: data?.finanzamtsnummer ?? '' })
                    setIsFinanzamtModalVisible(true)
                  }}
                >
                  {t('general.edit')}
                </Button>
              }
            >
              <SettingsReadOnlyField
                label={t('settings.finanzamtsnummer.label')}
                value={data?.finanzamtsnummer}
              />
            </SettingsDataCard>
            <SettingsDataCard
              icon={<FaUniversity />}
              title={t('settings.finanzkasse.title')}
              action={
                <Button
                  isSecondary
                  size="sm"
                  onClick={() => {
                    resetIban({
                      finanzkasse_iban: data?.finanzkasse_iban ?? '',
                      finanzkasse_recipient_name: data?.finanzkasse_recipient_name ?? ''
                    })
                    setIsIbanModalVisible(true)
                  }}
                >
                  {t('general.edit')}
                </Button>
              }
            >
              <SettingsReadOnlyField
                label={t('settings.finanzkasseIban.label')}
                value={
                  data?.finanzkasse_iban
                    ? tryFormatIBAN(compactIBAN(data.finanzkasse_iban))
                    : undefined
                }
              />
              <SettingsReadOnlyField
                label={t('settings.finanzkasseRecipient.label')}
                value={data?.finanzkasse_recipient_name}
              />
            </SettingsDataCard>
            <SettingsDataCard
              icon={<FaClock />}
              title={t('settings.dauerfristverlaengerung.title')}
            >
              <DauerfristRow>
                <DauerfristLabel>
                  <label htmlFor="has_dauerfristverlaengerung">
                    {t('settings.dauerfristverlaengerung.label')}
                  </label>
                  <TextTooltip tooltip={t('settings.dauerfristverlaengerung.info')} placement="top">
                    <HelpIcon aria-hidden="true">
                      <FaQuestionCircle />
                    </HelpIcon>
                  </TextTooltip>
                </DauerfristLabel>
                <Switch
                  id="has_dauerfristverlaengerung"
                  height={24}
                  disabled={dauerfristMutation.isPending}
                  checked={Boolean(data?.has_dauerfristverlaengerung)}
                  onChange={e => dauerfristMutation.mutate(e.currentTarget.checked)}
                />
              </DauerfristRow>
            </SettingsDataCard>
          </>
        )}

        <SettingsDataCard
          icon={<FaPercentage />}
          title={t('settings.accountsPage.vatPostingMethod.title')}
          action={
            <Button
              isSecondary
              size="sm"
              onClick={() => {
                reset(getDefaultValues(data))
                setIsVatMethodModalVisible(true)
              }}
            >
              {t('general.edit')}
            </Button>
          }
        >
          <SettingsReadOnlyField
            label={t('settings.accountsPage.vatPostingMethod.title')}
            value={selectedVatPostingMethodLabel}
          />
          <FeatureWrapper feature="vat_not_due_release">
            <SettingsReadOnlyField
              label={t('settings.accountsPage.vatReportingMethod.title')}
              value={selectedVatReportingMethodLabel}
            />
          </FeatureWrapper>
        </SettingsDataCard>
      </CardsWrapper>

      <ModalV2
        header={t('settings.vatPeriod')}
        isVisible={isVatPeriodModalVisible}
        handleOnClose={() => setIsVatPeriodModalVisible(false)}
        width={500}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setIsVatPeriodModalVisible(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              intent="success"
              icon={<FaSave />}
              type="submit"
              form="vat-period-settings-form"
              disabled={!isDirty || updateMutation.isPending}
              showSpinner={isSubmitting || updateMutation.isPending}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <form id="vat-period-settings-form" onSubmit={onSubmit}>
          <Controller
            control={control}
            name="default_vat_period"
            render={({ field: { ref, value, onChange, onBlur, name } }) => (
              <Select
                ref={ref}
                name={name}
                label={t('settings.vatPeriod')}
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
              >
                {VAT_PERIOD_OPTIONS.map(({ labelKey, value }) => (
                  <option key={value} value={value}>
                    {t(labelKey)}
                  </option>
                ))}
              </Select>
            )}
          />
        </form>
      </ModalV2>

      <ModalV2
        header={t('settings.accountsPage.vatPostingMethod.title')}
        isVisible={isVatMethodModalVisible}
        handleOnClose={() => setIsVatMethodModalVisible(false)}
        width={500}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setIsVatMethodModalVisible(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              intent="success"
              icon={<FaSave />}
              type="submit"
              form="vat-method-settings-form"
              disabled={
                !isDirty ||
                updateMutation.isPending ||
                isLoadingVatPostingMethods ||
                isLoadingVatReportingMethods
              }
              showSpinner={isSubmitting || updateMutation.isPending}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <form id="vat-method-settings-form" onSubmit={onSubmit}>
          <Controller
            control={control}
            name="vat_posting_method"
            render={({ field: { ref, value, onChange, onBlur, name } }) => (
              <Select
                ref={ref}
                name={name}
                label={t('settings.accountsPage.vatPostingMethod.title')}
                info={t('settings.accountsPage.vatPostingMethod.info')}
                value={value ?? ''}
                onChange={onChange}
                onBlur={onBlur}
              >
                {vatPostingMethods?.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          />
          <FeatureWrapper feature="vat_not_due_release">
            <Controller
              control={control}
              name="vat_reporting_method"
              render={({ field: { ref, value, onChange, onBlur, name } }) => (
                <Select
                  ref={ref}
                  name={name}
                  label={t('settings.accountsPage.vatReportingMethod.title')}
                  info={t('settings.accountsPage.vatReportingMethod.info')}
                  value={value ?? ''}
                  onChange={onChange}
                  onBlur={onBlur}
                >
                  {vatReportingMethods?.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              )}
            />
          </FeatureWrapper>
        </form>
      </ModalV2>

      <ModalV2
        header={t('settings.finanzamtsnummer.title')}
        isVisible={isFinanzamtModalVisible}
        handleOnClose={() => setIsFinanzamtModalVisible(false)}
        width={500}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setIsFinanzamtModalVisible(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              intent="success"
              icon={<FaSave />}
              type="submit"
              form="finanzamtsnummer-settings-form"
              disabled={!isFinanzamtDirty || finanzamtMutation.isPending}
              showSpinner={isFinanzamtSubmitting || finanzamtMutation.isPending}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <form id="finanzamtsnummer-settings-form" onSubmit={onFinanzamtSubmit}>
          <Input
            label={t('settings.finanzamtsnummer.label')}
            info={t('settings.finanzamtsnummer.info')}
            placeholder={t('settings.finanzamtsnummer.placeholder')}
            errors={get(finanzamtErrors, 'finanzamtsnummer')}
            {...registerFinanzamt('finanzamtsnummer', {
              validate: value => {
                const digits = (value ?? '').replace(/\D/g, '')
                if (!digits || digits.length === 4) {
                  return true
                }
                return t('settings.finanzamtsnummer.invalid')
              }
            })}
          />
        </form>
      </ModalV2>

      <ModalV2
        header={t('settings.finanzkasse.title')}
        isVisible={isIbanModalVisible}
        handleOnClose={() => setIsIbanModalVisible(false)}
        width={500}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setIsIbanModalVisible(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              intent="success"
              icon={<FaSave />}
              type="submit"
              form="finanzkasse-iban-settings-form"
              disabled={!isIbanDirty || ibanMutation.isPending}
              showSpinner={isIbanSubmitting || ibanMutation.isPending}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <form id="finanzkasse-iban-settings-form" onSubmit={onIbanSubmit}>
          <Input
            label={t('settings.finanzkasseIban.label')}
            info={t('settings.finanzkasseIban.info')}
            placeholder={t('settings.finanzkasseIban.placeholder')}
            required
            errors={get(ibanErrors, 'finanzkasse_iban')}
            {...registerIban('finanzkasse_iban', {
              validate: validateIban
            })}
          />
          <Input
            label={t('settings.finanzkasseRecipient.label')}
            info={t('settings.finanzkasseRecipient.info')}
            placeholder={t('settings.finanzkasseRecipient.placeholder')}
            required
            errors={get(ibanErrors, 'finanzkasse_recipient_name')}
            {...registerIban('finanzkasse_recipient_name', {
              required: t('validation.required'),
              maxLength: { value: 70, message: t('settings.finanzkasseRecipient.maxLength') },
              validate: value => Boolean(value?.trim()) || t('validation.required')
            })}
          />
        </form>
      </ModalV2>
    </SettingsSubPageLayout>
  )
}

const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  justify-content: space-between;
  width: 100%;
`

const CardsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const DauerfristRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const DauerfristLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
  font-weight: 500;
`

const HelpIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.iconGray};
  cursor: help;
`
